import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CopyIcon, DownloadIcon } from "./Icons.tsx";
import { generatePDFHtml } from "./ActionButtons.utils.ts";

import type { Source } from "../../types";

interface ActionButtonsProps {
  content: string;
  sources?: Source[];
}

export const ActionButtons: React.FC<ActionButtonsProps> = ({ content, sources }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportPDF = () => {
    const htmlContent = generatePDFHtml(content, t, sources);

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      // Wait for resources (fonts) to load before printing
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 300);
      };
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 gap-y-3 mt-4 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
      <button
        onClick={handleCopy}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
          copied
            ? "bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400"
            : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:border-blue-400 hover:text-blue-500"
        }`}
      >
        <CopyIcon />
        {copied ? t("common.copied", "Kopeeritud!") : t("common.copy", "Kopeeri")}
      </button>
      <button
        onClick={handleExportPDF}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-medium hover:border-blue-400 hover:text-blue-500 transition-all shadow-sm"
      >
        <DownloadIcon />
        {t("common.export_pdf", "Ekspordi PDF")}
      </button>
    </div>
  );
};
